import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchFormsForCycle, setCalibratedScore, logDiscretionAdjustment } from '../services/reviewFormService';
import { fetchCycles, fetchCycle } from '../services/reviewCycleService';
import { fetchTemplate } from '../services/reviewTemplateService';
import ScoreBar from '../components/ScoreBar';
import WorkflowStatusBadge from '../components/WorkflowStatusBadge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { ReviewForm, ReviewCycle, ReviewTemplate } from '../types';

const SCORE_BUCKETS = [
  { range: '1.0–1.9', min: 1.0, max: 1.9 },
  { range: '2.0–2.9', min: 2.0, max: 2.9 },
  { range: '3.0–3.4', min: 3.0, max: 3.4 },
  { range: '3.5–3.9', min: 3.5, max: 3.9 },
  { range: '4.0–4.4', min: 4.0, max: 4.4 },
  { range: '4.5–5.0', min: 4.5, max: 5.0 },
];

const CalibrationPanel: React.FC = () => {
  const { cycleId } = useParams<{ cycleId?: string }>();
  const { user } = useAuth();
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState(cycleId ?? '');
  const [forms, setForms] = useState<ReviewForm[]>([]);
  const [template, setTemplate] = useState<ReviewTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [calibrations, setCalibrations] = useState<Record<string, string>>({}); // formId → score string
  const [discretionModal, setDiscretionModal] = useState<{ form: ReviewForm } | null>(null);
  const [delta, setDelta] = useState('');
  const [discretionReason, setDiscretionReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCycles().then(setCycles).catch(() => {/* cycle list stays empty */});
  }, []);

  useEffect(() => {
    if (!selectedCycleId) return;
    loadCycleData(selectedCycleId);
  }, [selectedCycleId]);

  const loadCycleData = async (cid: string) => {
    setLoading(true);
    try {
      const [f, cycle] = await Promise.all([fetchFormsForCycle(cid), fetchCycle(cid)]);
      setForms(f);
      if (cycle) {
        const t = await fetchTemplate(cycle.templateId);
        setTemplate(t);
      }
      // Init calibration inputs from existing values
      const initCal: Record<string, string> = {};
      f.forEach(form => {
        initCal[form.id] = form.calibratedScore?.toFixed(2) ?? form.computedScore?.toFixed(2) ?? '';
      });
      setCalibrations(initCal);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSave = async () => {
    if (!user) return;
    setBusy(true);
    setError('');
    try {
      await Promise.all(
        Object.entries(calibrations).map(([formId, val]) => {
          const num = parseFloat(val);
          if (!isNaN(num)) return setCalibratedScore(formId, num, user.id);
          return Promise.resolve();
        }),
      );
      await loadCycleData(selectedCycleId);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDiscretionSubmit = async () => {
    if (!discretionModal || !user || !template) return;
    const form = discretionModal.form;
    const d = parseFloat(delta);
    if (isNaN(d)) { setError('Enter a valid delta.'); return; }
    if (!discretionReason.trim()) { setError('Reason is required.'); return; }
    if (Math.abs(d) > template.maxDiscretionDelta) {
      setError(`Delta cannot exceed ±${template.maxDiscretionDelta}.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const baseScore = form.calibratedScore ?? form.computedScore ?? 0;
      await logDiscretionAdjustment(
        form.id, form.revieweeId,
        baseScore, d, discretionReason,
        user.id, template.maxDiscretionDelta, template.scoringScale,
      );
      setDiscretionModal(null);
      setDelta('');
      setDiscretionReason('');
      await loadCycleData(selectedCycleId);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  // Build histogram data
  const histData = SCORE_BUCKETS.map(bucket => ({
    name: bucket.range,
    count: forms.filter(f => {
      const s = f.calibratedScore ?? f.computedScore ?? 0;
      return s >= bucket.min && s <= bucket.max;
    }).length,
  }));

  const readyForms = forms.filter(f =>
    ['manager_submitted', 'calibration', 'approved', 'signed_off'].includes(f.status),
  );

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Calibration Panel</h1>

      {/* Cycle selector */}
      <div className="flex items-center gap-3">
        <select
          value={selectedCycleId}
          onChange={e => setSelectedCycleId(e.target.value)}
          className="bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-brand-accent focus:outline-none"
        >
          <option value="">— select cycle —</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading && <p className="text-gray-500 animate-pulse">Loading…</p>}

      {!loading && selectedCycleId && (
        <>
          {/* Score distribution chart */}
          {readyForms.length > 0 && (
            <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Score Distribution</h2>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={histData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                    labelStyle={{ color: '#e2e8f0' }}
                    itemStyle={{ color: '#94a3b8' }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {histData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.count > 0 ? '#3b82f6' : '#334155'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Calibration table */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                {readyForms.length} Forms Ready · {forms.length} Total
              </h2>
              <button
                onClick={handleBulkSave}
                disabled={busy}
                className="bg-brand-accent hover:bg-blue-500 text-gray-900 text-xs font-semibold px-4 py-1.5 rounded-lg transition disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save All Calibrations'}
              </button>
            </div>

            {error && <p className="text-red-600 text-sm px-4 py-2">{error}</p>}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="text-left p-3">Name</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-center p-3">Computed</th>
                    <th className="text-center p-3">Score Bar</th>
                    <th className="text-center p-3 w-28">Calibrated</th>
                    <th className="text-center p-3">Discretion</th>
                    <th className="text-center p-3">Final</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map(f => (
                    <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 text-gray-900 font-medium">{f.revieweeName}</td>
                      <td className="p-3 text-center">
                        <WorkflowStatusBadge status={f.status} size="sm" />
                      </td>
                      <td className="p-3 text-center text-gray-700">
                        {f.computedScore?.toFixed(2) ?? '—'}
                      </td>
                      <td className="p-3 w-32">
                        {f.computedScore != null && (
                          <ScoreBar score={f.computedScore} showValue={false} size="sm" />
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <input
                          type="number"
                          step="0.1"
                          min="1"
                          max={template?.scoringScale ?? 5}
                          value={calibrations[f.id] ?? ''}
                          onChange={e => setCalibrations(prev => ({ ...prev, [f.id]: e.target.value }))}
                          className="w-20 bg-white text-gray-900 text-sm text-center border border-gray-200 rounded px-2 py-1 focus:border-brand-accent focus:outline-none"
                        />
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => { setDiscretionModal({ form: f }); setDelta(''); setDiscretionReason(''); setError(''); }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          {f.discretionAdjustment != null
                            ? (f.discretionAdjustment >= 0 ? '+' : '') + f.discretionAdjustment.toFixed(2)
                            : 'Add adj.'}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`font-bold ${f.finalScore != null ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {f.finalScore?.toFixed(2) ?? '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {forms.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-gray-400 text-sm">No forms found for this cycle.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Discretion Adjustment Modal */}
      {discretionModal && template && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Discretion Adjustment</h2>
            <p className="text-gray-500 text-sm">
              Reviewee: <strong className="text-gray-900">{discretionModal.form.revieweeName}</strong>
            </p>
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Calibrated Score</span>
                <span className="text-gray-900 font-semibold">{discretionModal.form.calibratedScore?.toFixed(2) ?? '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Max delta (±)</span>
                <span className="text-gray-900">{template.maxDiscretionDelta}</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Delta (±{template.maxDiscretionDelta} max)
              </label>
              <input
                type="number"
                step="0.1"
                placeholder={`e.g. 0.3 or -0.2`}
                value={delta}
                onChange={e => setDelta(e.target.value)}
                className="w-full bg-white text-gray-900 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Reason (required)</label>
              <textarea
                value={discretionReason}
                onChange={e => setDiscretionReason(e.target.value)}
                rows={3}
                placeholder="Provide clear justification for the discretion adjustment…"
                className="w-full bg-white text-gray-900 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-accent focus:outline-none resize-none"
              />
            </div>
            {delta && discretionModal.form.calibratedScore != null && (
              <div className="bg-gray-50 rounded p-2 text-xs text-gray-500">
                Preview final score:{' '}
                <strong className="text-emerald-600 text-sm">
                  {Math.max(1, Math.min(template.scoringScale, discretionModal.form.calibratedScore + parseFloat(delta || '0'))).toFixed(2)}
                </strong>
              </div>
            )}
            {error && <p className="text-red-600 text-xs">{error}</p>}
            <div className="flex gap-3">
              <button onClick={handleDiscretionSubmit} disabled={busy} className="bg-brand-accent hover:bg-blue-500 text-gray-900 font-semibold px-5 py-2 rounded-lg text-sm transition disabled:opacity-50">
                {busy ? 'Applying…' : 'Apply Adjustment'}
              </button>
              <button onClick={() => { setDiscretionModal(null); setError(''); }} className="text-gray-500 hover:text-gray-900 text-sm px-4">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalibrationPanel;
